(() => {
  const FEEDBACK_KEY = 'recovercann_reorder_feedback';
  const EXPECTED_KEY = 'recovercann_reorder_expected';
  const SESSION_KEY = 'recovercann_reservation_session_id';
  const EXPECTED_TTL_MS = 10 * 60 * 1000;

  function readSessionStorage(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function writeSessionStorage(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // noop
    }
  }

  function removeSessionStorage(key) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // noop
    }
  }

  function readLocalStorage(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function writeLocalStorage(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // noop
    }
  }

  function getReservationSessionId() {
    let sessionId = readLocalStorage(SESSION_KEY);
    if (sessionId) return sessionId;

    sessionId = `rc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    writeLocalStorage(SESSION_KEY, sessionId);
    return sessionId;
  }

  function parseReorderItems(raw) {
    if (!raw || typeof raw !== 'string') return [];

    return raw
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((entry) => {
        const [variantIdRaw, quantityRaw, productIdRaw] = entry.split(':');
        const variantId = Number(variantIdRaw);
        const quantity = Number(quantityRaw);
        const productId = Number(productIdRaw);

        if (!Number.isFinite(variantId) || !Number.isFinite(quantity) || quantity <= 0) {
          return null;
        }

        return {
          variantId,
          quantity,
          productId: Number.isFinite(productId) ? productId : null,
        };
      })
      .filter(Boolean);
  }

  function setFeedback(payload) {
    writeSessionStorage(FEEDBACK_KEY, JSON.stringify(payload));
  }

  function clearFeedback() {
    removeSessionStorage(FEEDBACK_KEY);
  }

  function readFeedback() {
    const raw = readSessionStorage(FEEDBACK_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  function renderFeedback() {
    const feedback = readFeedback();
    const containers = document.querySelectorAll('[data-rc-reorder-feedback]');
    if (!containers.length) return;

    containers.forEach((container) => {
      const messageNode = container.querySelector('[data-rc-reorder-feedback-message]');
      const linkNode = container.querySelector('[data-rc-reorder-feedback-link]');
      const removeButton = container.querySelector('[data-rc-reorder-remove-invalid]');

      if (!feedback || !feedback.message) {
        container.hidden = true;
        return;
      }

      if (messageNode) {
        messageNode.textContent = feedback.message;
      }

      if (linkNode) {
        linkNode.textContent = feedback.linkText || 'Review order history';
        linkNode.setAttribute('href', feedback.linkHref || '/account#orders');
      }

      if (removeButton) {
        const invalidVariants = Array.isArray(feedback.invalidVariants)
          ? feedback.invalidVariants.map((variantId) => Number(variantId)).filter((variantId) => Number.isFinite(variantId))
          : [];
        if (invalidVariants.length > 0) {
          removeButton.hidden = false;
          removeButton.disabled = false;
          removeButton.dataset.invalidVariants = invalidVariants.join(',');
        } else {
          removeButton.hidden = true;
          removeButton.dataset.invalidVariants = '';
        }
      }

      container.hidden = false;
    });
  }

  async function removeInvalidItems(button) {
    const variants = (button.dataset.invalidVariants || '')
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isFinite(value));

    if (!variants.length) return;

    const updates = {};
    variants.forEach((variantId) => {
      updates[String(variantId)] = 0;
    });

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Removing…';

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        throw new Error(`Cart update failed with status ${response.status}`);
      }

      clearFeedback();
      renderFeedback();
      window.location.reload();
    } catch (error) {
      console.error('[RecoverCann Reorder] Failed to remove invalid items:', error);
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  function bindFeedbackActions() {
    const buttons = document.querySelectorAll('[data-rc-reorder-remove-invalid]');
    if (!buttons.length) return;

    buttons.forEach((button) => {
      if (button.dataset.boundClick === 'true') return;
      button.dataset.boundClick = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        removeInvalidItems(button);
      });
    });
  }

  function openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!cartDrawer) return;
    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    const trigger = cartDrawer.querySelector('[data-testid="cart-drawer-trigger"]');
    trigger?.click();
  }

  function getCustomerIdFromButton(button) {
    const fromButton = button.dataset.customerId;
    if (fromButton) return fromButton;

    const fromStatus = document.querySelector('.recovercann-cart-status[data-customer-id]')?.getAttribute('data-customer-id');
    return fromStatus || null;
  }

  function getApiBaseFromButton(button) {
    return button.dataset.apiBase
      || document.querySelector('[data-api-base]')?.getAttribute('data-api-base')
      || 'https://recovercann-shopify.vercel.app';
  }

  async function addReorderItemsToCart(items) {
    const failedVariants = [];

    for (const item of items) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            id: item.variantId,
            quantity: item.quantity,
          }),
        });

        if (!response.ok) {
          failedVariants.push(item.variantId);
        }
      } catch {
        failedVariants.push(item.variantId);
      }
    }

    return { failedVariants };
  }

  function rememberExpectedReorder(items, context = {}) {
    writeSessionStorage(EXPECTED_KEY, JSON.stringify({
      timestamp: Date.now(),
      items,
      customerId: context.customerId || null,
      apiBase: context.apiBase || null,
    }));
  }

  async function getReorderInvalidVariants({ expected, cart }) {
    const customerId = expected?.customerId;
    const apiBase = expected?.apiBase;
    if (!customerId || !apiBase) return [];

    const cartItems = Array.isArray(cart?.items)
      ? cart.items.map((item) => ({
        variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
        productId: item.product_id ? `gid://shopify/Product/${item.product_id}` : null,
        quantity: Number(item.quantity || 0),
      })).filter((item) => item.quantity > 0)
      : [];

    if (!cartItems.length) return [];

    try {
      const response = await fetch(`${apiBase}/api/cart/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-RecoverCann-Session-Id': getReservationSessionId(),
        },
        body: JSON.stringify({
          customerId,
          cartItems,
          mode: 'full',
        }),
      });

      if (!response.ok) return [];

      const validation = await response.json();
      const thcCap = Number(validation?.prescription?.thcCap || 0);
      const validationItems = Array.isArray(validation?.cart?.items) ? validation.cart.items : [];

      if (thcCap <= 0 || !validationItems.length) return [];

      const invalid = validationItems
        .filter((item) => Number(item?.thcLevel || 0) > thcCap)
        .map((item) => {
          const value = item?.variantId;
          if (typeof value !== 'string') return null;
          const id = Number(value.split('/').pop());
          return Number.isFinite(id) ? id : null;
        })
        .filter((id) => id !== null);

      return Array.from(new Set(invalid));
    } catch {
      return [];
    }
  }

  async function compareExpectedVsActualCart() {
    const raw = readSessionStorage(EXPECTED_KEY);
    if (!raw) return;

    let expected;
    try {
      expected = JSON.parse(raw);
    } catch {
      removeSessionStorage(EXPECTED_KEY);
      return;
    }

    if (!expected?.timestamp || Date.now() - expected.timestamp > EXPECTED_TTL_MS) {
      removeSessionStorage(EXPECTED_KEY);
      return;
    }

    const expectedItems = Array.isArray(expected.items) ? expected.items : [];
    if (!expectedItems.length) {
      removeSessionStorage(EXPECTED_KEY);
      return;
    }

    try {
      const cartResponse = await fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } });
      if (!cartResponse.ok) {
        removeSessionStorage(EXPECTED_KEY);
        return;
      }

      const cart = await cartResponse.json();
      const actualByVariant = new Map((cart.items || []).map((item) => [Number(item.variant_id), Number(item.quantity || 0)]));

      const missingItems = expectedItems.filter((item) => {
        const currentQty = actualByVariant.get(Number(item.variantId)) || 0;
        return currentQty < Number(item.quantity || 0);
      });

      const invalidVariants = await getReorderInvalidVariants({ expected, cart });

      if (invalidVariants.length > 0) {
        setFeedback({
          message: 'Some items in your re-order are not valid for your account right now.',
          linkText: 'Review order history',
          linkHref: '/account#orders',
          invalidVariants,
        });
        renderFeedback();
        openCartDrawer();
        return;
      }

      if (missingItems.length > 0) {
        setFeedback({
          message: 'Some items from your re-order were unavailable and were not added to your bag.',
          linkText: 'Review order history',
          linkHref: '/account#orders',
        });
        renderFeedback();
        openCartDrawer();
      } else {
        clearFeedback();
        renderFeedback();
      }
    } finally {
      removeSessionStorage(EXPECTED_KEY);
    }
  }

  function bindReorderButtons() {
    const buttons = document.querySelectorAll('a.rc-reorder-button[data-reorder-items]');
    if (!buttons.length) return;

    buttons.forEach((button) => {
      if (button.dataset.reorderBound === 'true') return;
      button.dataset.reorderBound = 'true';

      button.addEventListener('click', async (event) => {
        const items = parseReorderItems(button.dataset.reorderItems || '');
        if (!items.length) return;

        event.preventDefault();

        const originalText = button.textContent;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');
        button.textContent = 'Adding items…';

        try {
          const addResult = await addReorderItemsToCart(items);

          if (addResult.failedVariants.length > 0) {
            setFeedback({
              message: 'Some re-order items were out of stock or unavailable and could not be added.',
              linkText: 'Review order history',
              linkHref: '/account#orders',
            });
          } else {
            clearFeedback();
          }

          window.location.assign('/cart');
        } catch (error) {
          console.warn('[RecoverCann Reorder] Re-order add failed:', error);
          setFeedback({
            message: 'We could not complete your re-order automatically. Please add items to your bag from order history.',
            linkText: 'Review order history',
            linkHref: '/account#orders',
          });
          renderFeedback();
          openCartDrawer();
        } finally {
          button.removeAttribute('aria-disabled');
          button.classList.remove('is-loading');
          button.textContent = originalText;
        }
      });
    });
  }

  function init() {
    bindReorderButtons();
    bindFeedbackActions();
    renderFeedback();
    compareExpectedVsActualCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    bindReorderButtons();
    bindFeedbackActions();
    renderFeedback();
  });
})();
